import { useState } from 'react';
import {
  ShoppingCart,
  ShoppingBag,
  X,
  Trash2,
  Plus,
  Minus,
  LayoutGrid,
  List,
  Minimize2,
  Briefcase,
  GitBranch,
  Truck,
  FileCheck as FileCheck2,
  Loader as Loader2,
  CircleAlert as AlertCircle,
  Package,
  Warehouse,
} from 'lucide-react';
import type { CartItem } from './B2BCartContext';

// ─── Types ───────────────────────────────────────────────────────────────────

type ViewMode = 'grid' | 'list';

export interface ExpandedCartProps {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  setQuantity: (productId: string, quantity: number) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  onClose: () => void;
  onCollapse: () => void;
  submitting: boolean;

  // Session context
  isAgent: boolean;
  isClient: boolean;
  activeClientRecord: { name: string; company_id: string } | null;
  agentRecord: { name: string; email?: string | null } | null;
  supplierNames: Record<string, string>;

  // VAT
  cartIsVatLiable: boolean;
  vatRate: number | null;

  // Order submission
  orderCtx: unknown;
  onSubmitOrder: () => void;
  handleSignOut: () => void;

  // Consignment
  cartIsDualMode: boolean;
  cartIsConsignmentOnly: boolean;
  orderType: 'standard' | 'consignment';
  setOrderType: (t: 'standard' | 'consignment') => void;

  // Van Sale
  instantDelivery: boolean;
  setInstantDelivery: (fn: (v: boolean) => boolean) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Grid Card ───────────────────────────────────────────────────────────────

function GridCard({
  item,
  onSetQty,
  onRemove,
  disabled,
}: {
  item: CartItem;
  onSetQty: (qty: number) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { product, quantity } = item;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden
                    flex flex-col transition-shadow hover:shadow-md">
      {/* Image */}
      <div className="h-32 sm:h-40 bg-gray-50 flex items-center justify-center overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <ShoppingBag className="w-10 h-10 text-gray-200" />
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
          {product.name}
        </p>
        <p className="text-xs text-gray-400 font-mono" dir="ltr">{product.sku}</p>
        {product.supplier_name && (
          <p className="text-[10px] text-gray-400">{product.supplier_name}</p>
        )}
        <div className="mt-auto pt-2 flex items-end justify-between gap-2">
          <div>
            <p className="text-[10px] text-gray-400">מחיר יח׳</p>
            <p className="text-xs font-medium text-gray-600">{formatPrice(product.price)}</p>
          </div>
          <div className="text-left">
            <p className="text-[10px] text-gray-400">סה״כ</p>
            <p className="text-sm font-bold text-primary-600">
              {formatPrice(product.price * quantity)}
            </p>
          </div>
        </div>
      </div>

      {/* Footer: qty stepper + remove */}
      <div className="px-3 pb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSetQty(quantity - 1)}
            disabled={disabled}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200
                       text-gray-500 hover:bg-gray-100 active:scale-90 transition-all disabled:opacity-40"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={e => {
              const n = parseInt(e.target.value, 10);
              if (!isNaN(n) && n >= 1) onSetQty(n);
            }}
            disabled={disabled}
            className="w-14 h-8 text-center text-sm font-bold border border-gray-200
                       rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300
                       focus:border-transparent disabled:opacity-40"
          />
          <button
            type="button"
            onClick={() => onSetQty(quantity + 1)}
            disabled={disabled}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200
                       text-gray-500 hover:bg-gray-100 active:scale-90 transition-all disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300
                     hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── List Row ────────────────────────────────────────────────────────────────

function ListRow({
  item,
  onSetQty,
  onRemove,
  disabled,
}: {
  item: CartItem;
  onSetQty: (qty: number) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { product, quantity } = item;
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      {/* Image */}
      <td className="py-2.5 px-3">
        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-gray-300" />
            </div>
          )}
        </div>
      </td>
      {/* Product */}
      <td className="py-2.5 px-3">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{product.name}</p>
        {product.supplier_name && (
          <p className="text-[10px] text-gray-400 mt-0.5">{product.supplier_name}</p>
        )}
      </td>
      {/* SKU */}
      <td className="py-2.5 px-3 hidden sm:table-cell">
        <span className="text-xs text-gray-400 font-mono" dir="ltr">{product.sku}</span>
      </td>
      {/* Unit price */}
      <td className="py-2.5 px-3 text-sm text-gray-600 hidden md:table-cell">
        {formatPrice(product.price)}
      </td>
      {/* Quantity */}
      <td className="py-2.5 px-3">
        <input
          type="number"
          min={1}
          value={quantity}
          onChange={e => {
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n) && n >= 0) onSetQty(n);
          }}
          disabled={disabled}
          className="w-16 h-8 text-center text-sm font-bold border border-gray-200
                     rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-300
                     focus:border-transparent disabled:opacity-40"
        />
      </td>
      {/* Line total */}
      <td className="py-2.5 px-3 text-sm font-bold text-primary-600 whitespace-nowrap">
        {formatPrice(product.price * quantity)}
      </td>
      {/* Remove */}
      <td className="py-2.5 px-3">
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300
                     hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function B2BExpandedCart(props: ExpandedCartProps) {
  const {
    items,
    totalItems,
    totalPrice,
    setQuantity,
    removeItem,
    clearCart,
    onClose,
    onCollapse,
    submitting,
    isAgent,
    isClient,
    activeClientRecord,
    agentRecord,
    supplierNames,
    cartIsVatLiable,
    vatRate,
    orderCtx,
    onSubmitOrder,
    handleSignOut,
    cartIsDualMode,
    cartIsConsignmentOnly,
    orderType,
    setOrderType,
    instantDelivery,
    setInstantDelivery,
  } = props;

  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={() => !submitting && onClose()}
      />

      {/* Full-screen overlay */}
      <div
        dir="rtl"
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-8"
      >
        <div className="bg-white rounded-3xl shadow-2xl w-full h-full max-w-6xl max-h-full
                        flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100
                          bg-white shrink-0">
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-5 h-5 text-primary-600" />
              <h2 className="font-bold text-gray-900 text-lg">
                עגלת קניות
                {totalItems > 0 && (
                  <span className="mr-2 text-sm font-normal text-gray-500">
                    ({totalItems} פריטים)
                  </span>
                )}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              {/* View mode toggle */}
              {items.length > 0 && (
                <div className="flex items-center bg-gray-100 rounded-xl p-0.5">
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                      viewMode === 'grid'
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700',
                    ].join(' ')}
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">כרטיסים</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={[
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                      viewMode === 'list'
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700',
                    ].join(' ')}
                  >
                    <List className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">רשימה</span>
                  </button>
                </div>
              )}

              {/* Collapse back to narrow drawer */}
              <button
                type="button"
                onClick={onCollapse}
                disabled={submitting}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100
                           rounded-lg transition-colors disabled:opacity-40"
                title="כווץ"
              >
                <Minimize2 className="w-4.5 h-4.5" />
              </button>

              {/* Close entirely */}
              <button
                type="button"
                onClick={() => !submitting && onClose()}
                disabled={submitting}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100
                           rounded-lg transition-colors disabled:opacity-40"
                title="סגור"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* ── Agent / Client banners ──────────────────────────────────── */}
          <div className="px-4 sm:px-6 space-y-2 pt-3 shrink-0">
            {isAgent && activeClientRecord && (
              <div className="px-3 py-2 bg-orange-50 border border-orange-200
                              rounded-xl text-xs text-orange-700 flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 shrink-0" />
                <span>הזמנה עבור: <strong>{activeClientRecord.name}</strong></span>
              </div>
            )}
            {isClient && activeClientRecord && (
              <div className="px-3 py-2 bg-primary-50 border border-primary-200
                              rounded-xl text-xs text-primary-700 flex items-center gap-2">
                <GitBranch className="w-3.5 h-3.5 shrink-0" />
                <span>
                  הזמנה עבור: <strong>{activeClientRecord.name}</strong>
                  {supplierNames[activeClientRecord.company_id] && (
                    <span className="font-normal text-primary-500">
                      {' '}· {supplierNames[activeClientRecord.company_id]}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* ── Items area ──────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
                <ShoppingCart className="w-16 h-16 text-gray-200" />
                <p className="text-base font-medium">העגלה ריקה</p>
                <p className="text-sm text-gray-400">הוסף מוצרים מהקטלוג</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {items.map(item => (
                  <GridCard
                    key={item.product.id}
                    item={item}
                    onSetQty={qty => setQuantity(item.product.id, qty)}
                    onRemove={() => removeItem(item.product.id)}
                    disabled={submitting}
                  />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full text-right">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 font-medium">
                      <th className="py-2.5 px-3 w-14"></th>
                      <th className="py-2.5 px-3">מוצר</th>
                      <th className="py-2.5 px-3 hidden sm:table-cell">מק״ט</th>
                      <th className="py-2.5 px-3 hidden md:table-cell">מחיר יח׳</th>
                      <th className="py-2.5 px-3">כמות</th>
                      <th className="py-2.5 px-3">סה״כ</th>
                      <th className="py-2.5 px-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <ListRow
                        key={item.product.id}
                        item={item}
                        onSetQty={qty => setQuantity(item.product.id, qty)}
                        onRemove={() => removeItem(item.product.id)}
                        disabled={submitting}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Footer ──────────────────────────────────────────────────── */}
          {items.length > 0 && (
            <div className="border-t border-gray-100 bg-white px-4 sm:px-6 py-4 shrink-0">
              <div className="max-w-3xl mx-auto space-y-3">
                {/* Totals row */}
                <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      {cartIsVatLiable ? 'סה״כ לפני מע״מ' : 'סה״כ'}
                    </span>
                    <span className="font-bold text-gray-900 text-lg">
                      {formatPrice(totalPrice)}
                    </span>
                  </div>

                  {!cartIsVatLiable && (
                    <span className="text-xs text-gray-400">פטור ממע״מ</span>
                  )}
                  {cartIsVatLiable && vatRate != null && vatRate > 0 && (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-400 text-xs">
                        מע״מ ({vatRate}%) — {formatPrice(totalPrice * vatRate / 100)}
                      </span>
                      <span className="font-bold text-primary-700">
                        סה״כ כולל מע״מ: {formatPrice(totalPrice * (1 + vatRate / 100))}
                      </span>
                    </div>
                  )}
                  {cartIsVatLiable && (vatRate == null || vatRate === 0) && (
                    <span className="text-xs text-gray-400">מע״מ ייווסף בעת הפקת המסמך</span>
                  )}
                </div>

                {/* Warning when order context missing */}
                {!orderCtx && (
                  <div className="flex items-start gap-2 text-xs text-amber-700
                                  bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      פרטי הלקוח לא זוהו.{' '}
                      <button onClick={handleSignOut} className="underline hover:no-underline">
                        התנתק והתחבר מחדש
                      </button>{' '}
                      כדי לשלוח הזמנה.
                    </span>
                  </div>
                )}

                {/* Consignment chooser + Van Sale + Submit — horizontal on wide screens */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  {/* Left side: toggles */}
                  <div className="flex-1 flex flex-col gap-2">
                    {/* Consignment chooser */}
                    {cartIsConsignmentOnly && (
                      <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                          <Warehouse className="w-3.5 h-3.5" />
                          הזמנה זו תירשם כקונסגנציה (לקוח קונסגנציה בלבד)
                        </div>
                      </div>
                    )}

                    {cartIsDualMode && (
                      <div className="rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-3 space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                          <Warehouse className="w-3.5 h-3.5" />
                          בחירת סוג הזמנה
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setOrderType('standard')}
                            disabled={submitting}
                            aria-pressed={orderType === 'standard'}
                            className={[
                              'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl',
                              'text-xs font-semibold transition-all border-2 disabled:opacity-50',
                              orderType === 'standard'
                                ? 'bg-primary-600 border-primary-600 text-white shadow-sm'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-primary-300',
                            ].join(' ')}
                          >
                            <Package className="w-4 h-4" />
                            רכישה רגילה
                          </button>
                          <button
                            type="button"
                            onClick={() => setOrderType('consignment')}
                            disabled={submitting}
                            aria-pressed={orderType === 'consignment'}
                            className={[
                              'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl',
                              'text-xs font-semibold transition-all border-2 disabled:opacity-50',
                              orderType === 'consignment'
                                ? 'bg-amber-600 border-amber-600 text-white shadow-sm'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300',
                            ].join(' ')}
                          >
                            <Warehouse className="w-4 h-4" />
                            הזמנת קונסגנציה
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Van Sale toggle */}
                    {isAgent && agentRecord && (
                      <button
                        type="button"
                        onClick={() => setInstantDelivery(v => !v)}
                        disabled={submitting}
                        className={[
                          'w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2',
                          'text-right transition-all duration-200 disabled:opacity-50',
                          instantDelivery
                            ? 'border-orange-400 bg-orange-50 text-orange-800'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50',
                        ].join(' ')}
                        aria-pressed={instantDelivery}
                      >
                        <span
                          className={[
                            'relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 transition-colors duration-200',
                            instantDelivery ? 'bg-orange-500 border-orange-500' : 'bg-gray-200 border-gray-200',
                          ].join(' ')}
                        >
                          <span
                            className={[
                              'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200',
                              instantDelivery ? 'translate-x-5' : 'translate-x-0',
                            ].join(' ')}
                          />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-1.5 font-semibold text-sm">
                            <Truck className="w-4 h-4 shrink-0" />
                            אספקה מיידית — מכירה מהרכב
                          </span>
                          <span className="block text-xs mt-0.5 font-normal opacity-80">
                            {instantDelivery
                              ? 'תעודת משלוח תופק אוטומטית עם שליחת ההזמנה'
                              : 'הפק תעודת משלוח אוטומטית (Van Sale)'}
                          </span>
                        </span>
                        {instantDelivery && (
                          <FileCheck2 className="w-5 h-5 shrink-0 text-orange-600" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Right side: action buttons */}
                  <div className="flex flex-col gap-2 sm:w-64 shrink-0">
                    <button
                      onClick={onSubmitOrder}
                      disabled={submitting || !orderCtx}
                      className={[
                        'w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center',
                        'gap-2 transition-colors active:scale-[.98] disabled:cursor-not-allowed',
                        instantDelivery && isAgent
                          ? 'bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white'
                          : (cartIsConsignmentOnly || (cartIsDualMode && orderType === 'consignment'))
                            ? 'bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white'
                            : 'bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white',
                      ].join(' ')}
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />שולח הזמנה…</>
                      ) : instantDelivery && isAgent ? (
                        <><Truck className="w-4 h-4" />שלח + הפק תעודת משלוח</>
                      ) : cartIsConsignmentOnly ? (
                        <><Warehouse className="w-4 h-4" />שדר הזמנה (קונסגנציה)</>
                      ) : cartIsDualMode && orderType === 'consignment' ? (
                        <><Warehouse className="w-4 h-4" />שדר הזמנה (קונסגנציה)</>
                      ) : (
                        <><ShoppingCart className="w-4 h-4" />שדר הזמנה</>
                      )}
                    </button>

                    <button
                      onClick={clearCart}
                      disabled={submitting}
                      className="w-full py-2.5 rounded-2xl border border-gray-200 text-sm text-gray-500
                                 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                    >
                      נקה עגלה
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
